import { kioskApi } from '../axios.js';

export interface TranslationJob {
  id: string;
  shop_id: string;
  job_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_items: number;
  completed_items: number;
  progress_pct: number;
  metadata_json?: {
    target_language?: string;
    batch_size?: number;
    timeout_ms?: number;
    [key: string]: any;
  };
  result_json?: any;
  error_message?: string;
  started_at: string;
  completed_at?: string;
}

export interface SupportedLanguage {
  code: string;
  name: string;
  nativeName: string;
}

export interface TranslationApiShape {
  // Generate translations for all recipes in a shop
  generateTranslations(shopId: string, targetLanguage: string): Promise<{ 
    message: string; 
    shopId: string; 
    targetLanguage: string; 
    status: string; 
  }>;
  
  // Get translation job status
  getJobStatus(shopId: string, targetLanguage?: string): Promise<{ jobs: TranslationJob[] }>;
  
  // Get recipes with translations
  getRecipesWithTranslations(shopId: string, language?: string): Promise<{ 
    recipes: any[]; 
    language?: string; 
  }>;
  
  // Update recipe translation
  updateRecipeTranslation(
    shopId: string, 
    recipeId: string, 
    fieldName: string, 
    language: string, 
    translatedText: string
  ): Promise<{ 
    message: string; 
    recipeId: string; 
    fieldName: string; 
    language: string; 
    translatedText: string; 
  }>;
  
  // Update ingredient translation
  updateIngredientTranslation(
    shopId: string, 
    ingredientId: string, 
    language: string, 
    translatedText: string
  ): Promise<{ 
    message: string; 
    ingredientId: string; 
    language: string; 
    translatedText: string; 
  }>;
  
  // Get supported languages
  getSupportedLanguages(): Promise<{ supportedLanguages: SupportedLanguage[] }>;
  
  // Debug: Get raw recipe data
  getRecipesDebug(shopId: string): Promise<{ recipes: any[]; count: number; sampleIngredients: any[] }>;
}

export const translationApi: TranslationApiShape = {
  generateTranslations: (shopId: string, targetLanguage: string) =>
    kioskApi.post(`/translations/${shopId}/generate`, { targetLanguage }),

  getJobStatus: (shopId: string, targetLanguage?: string) => {
    const params = targetLanguage ? { targetLanguage } : {};
    return kioskApi.get(`/translations/${shopId}/jobs`, { params });
  },

  getRecipesWithTranslations: (shopId: string, language?: string) => {
    const params = language ? { language } : {};
    return kioskApi.get(`/translations/${shopId}/recipes`, { params });
  },

  updateRecipeTranslation: (
    shopId: string, 
    recipeId: string, 
    fieldName: string, 
    language: string, 
    translatedText: string
  ) =>
    kioskApi.patch(`/translations/${shopId}/recipes/${recipeId}`, {
      fieldName,
      language,
      translatedText
    }),

  updateIngredientTranslation: (
    shopId: string, 
    ingredientId: string, 
    language: string, 
    translatedText: string
  ) =>
    kioskApi.patch(`/translations/${shopId}/ingredients/${ingredientId}`, {
      language,
      translatedText
    }),

  getSupportedLanguages: () =>
    kioskApi.get('/translations/languages'),

  getRecipesDebug: (shopId: string) =>
    kioskApi.get(`/translations/${shopId}/recipes/debug`)
};
